import { useMemo } from 'react';
import { Modal } from '@swtorbit/ui/react';
import {
  asTrlDifficulty,
  TRL_DIFFICULTIES,
  TRL_DIFFICULTY_EMOJI,
  TRL_DIFFICULTY_LABELS,
  type TrlDifficulty,
  type TrlImageAsset,
} from '@swtorbit/shared-socket-events';

interface StatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: TrlImageAsset[];
}

/** The rating columns, in order, paired with the glyph each is headed by. */
const DIFFICULTIES = TRL_DIFFICULTIES.map((value) => ({
  value,
  label: TRL_DIFFICULTY_LABELS[value],
  emoji: TRL_DIFFICULTY_EMOJI[value],
}));

type DiffValue = TrlDifficulty;

function countByDifficulty(imgs: TrlImageAsset[]): Record<DiffValue | 'none', number> {
  const counts: Record<DiffValue | 'none', number> = {
    easy: 0, medium: 0, hard: 0, very_hard: 0, extreme: 0, none: 0,
  };
  for (const img of imgs) {
    const key = asTrlDifficulty(img.difficulty);
    if (key) counts[key]++;
    else counts.none++;
  }
  return counts;
}

export function StatsModal({ isOpen, onClose, images }: StatsModalProps) {
  const ownImages = useMemo(() => images.filter((i) => i.isOwn), [images]);

  const overallCounts = useMemo(() => countByDifficulty(ownImages), [ownImages]);

  const planetRows = useMemo(() => {
    const map = new Map<string, TrlImageAsset[]>();
    for (const img of ownImages) {
      const area = img.area || '(unknown)';
      if (!map.has(area)) map.set(area, []);
      map.get(area)!.push(img);
    }
    return [...map.entries()]
      .map(([area, imgs]) => ({ area, counts: countByDifficulty(imgs), total: imgs.length }))
      .sort((a, b) => a.area.localeCompare(b.area));
  }, [ownImages]);

  return (
    <Modal open={isOpen} onClose={onClose} size="xl" title="My Screenshot Statistics">
        <div className="d-flex flex-column gap-sm">

          {/* Summary row */}
          <div className="d-flex gap-xs flex-wrap" style={{ justifyContent: 'center' }}>
            <div className="d-flex flex-column align-items-center p-xs rounded-sm" style={{ background: 'color-mix(in srgb, var(--color-white) 6%, transparent)', minWidth: '5rem', justifyContent: 'flex-end' }}>
              <span className="fs-2xl fw-bold">{ownImages.length}</span>
              <span className="fs-xs text-slate">Total</span>
            </div>
            {DIFFICULTIES.map((d) => (
              <div key={d.value} className="d-flex flex-column align-items-center p-xs rounded-sm" style={{ background: 'color-mix(in srgb, var(--color-white) 6%, transparent)', minWidth: '5rem' }}>
                <span className="fs-2xl">{d.emoji}</span>
                <span className="fs-xl fw-bold">{overallCounts[d.value]}</span>
                <span className="fs-xs text-slate">{d.label}</span>
              </div>
            ))}
            {overallCounts.none > 0 && (
              <div className="d-flex flex-column align-items-center p-xs rounded-sm" style={{ background: 'color-mix(in srgb, var(--color-white) 6%, transparent)', minWidth: '5rem' }}>
                <span className="fs-2xl">—</span>
                <span className="fs-xl fw-bold">{overallCounts.none}</span>
                <span className="fs-xs text-slate">No rating</span>
              </div>
            )}
          </div>

          {/* Planet breakdown */}
          {planetRows.length > 0 && (
            <div>
              <p className="fs-xs text-slate text-uppercase font-semibold m-0" style={{ marginBottom: '0.5rem' }}>By Planet / Area</p>
              <div className="overflow-y-auto" style={{ maxHeight: '55vh' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid color-mix(in srgb, var(--color-white) 12%, transparent)' }}>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--color-white)', fontWeight: 600 }}>Planet / Area</th>
                      {DIFFICULTIES.map((d) => (
                        <th key={d.value} style={{ textAlign: 'center', padding: '6px 8px', color: 'var(--color-grey-light)', fontWeight: 600 }}>
                          {d.emoji}
                        </th>
                      ))}
                      <th style={{ textAlign: 'center', padding: '6px 8px', color: 'var(--color-grey-light)', fontWeight: 600 }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {planetRows.map((row, idx) => (
                      <tr
                        key={row.area}
                        style={{
                          borderBottom: '1px solid color-mix(in srgb, var(--color-white) 6%, transparent)',
                          background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.03)',
                        }}
                      >
                        <td style={{ padding: '7px 8px', fontWeight: 500 }}>{row.area}</td>
                        {DIFFICULTIES.map((d) => (
                          <td key={d.value} style={{ textAlign: 'center', padding: '7px 8px' }}>
                            {row.counts[d.value] > 0 ? (
                              <span>{d.emoji} {row.counts[d.value]}</span>
                            ) : (
                              <span className="text-slate" style={{ opacity: 0.3 }}>—</span>
                            )}
                          </td>
                        ))}
                        <td style={{ textAlign: 'center', padding: '7px 8px', fontWeight: 600 }}>{row.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {ownImages.length === 0 && (
            <p className="text-slate fs-sm text-center m-0" style={{ padding: '2rem 0' }}>
              You have not uploaded any screenshots yet.
            </p>
          )}
        </div>
      </Modal>
  );
}
